import { useCallback } from 'react';
import useWaWorkplaceRepo, {
  ReservationParameters,
  SummaryParameters,
} from '../repository/WaWorkplaceRepo';
import { useWaWorkplaceStore } from '../store/WaWorkplaceStore';
import { useWaUserStore } from '../store/WaUserStore';
import { useWaFilterStore } from '../store/WaFilterStore';
import { useWaSummaryStore } from '../store/WaSummaryStore';
import useWaOptionsRepo, {
  WaOrganizationBranchOption,
  WaOrganizationOptions,
  WaOrganizationSalesOfficeOption,
  WaOrganizationSectionOption,
  WaOrganizationSquadOption,
} from '../repository/WaOptionsRepo';
import {
  WaBranchOption,
  WaSalesOfficeOption,
  WaSectionOption,
  WaSquadOption,
} from '../store/WaFilterStore';
import {
  WaWorkplaceFilterParameters,
  WaWorkplaceRequestParams,
} from '../store/models/workplace/WaWorkplaceFilterParameter';
import {
  BranchSituation,
  mapSingleBranchToSituation,
} from 'src/ui/wards/desktop/components/summary/branchSummary/BranchSummaryColumns';
import { t } from 'i18next';
import {
  BusinessSituation,
  mapSingleItemToBusinessSituation,
} from 'src/ui/wards/desktop/components/summary/businessSummary/BusinessSummaryColumns';
import {
  ReservationResponse,
  WorkplaceReservationsToCancel,
  WorkplaceReservationToCreate,
  WorkplaceReservationValidation,
} from '../store/models/workplace';
import { operationStatusCodes } from '../store/models/workplace/OperationStatus';
import { useWaDisasterStore } from '../store/WaRegionAndDisasterStore';
import { calculateTotalSummary } from './helper/calculateTotalBranchSummary';
import { calculateTotalSalesSummary } from './helper/calculateTotalSalesSummary';
import { WaDisasterAreaBranchResponse } from '../models/WaSummary';

function useWaWorkplaceService() {
  const {
    workplaceFilterParameters,
    setBranchOptions,
    setSalesOfficeOptions,
    setSectionOptions,
    setSquadOptions,
    setStatusOptions,
    setUserOfficeLocation,
  } = useWaFilterStore();
  const { updateWorkplacesStatus, setWorkplaces, setSelectedWorkplace } = useWaWorkplaceStore();
  const { setSummary } = useWaSummaryStore();
  const {
    getWorkplacesRequest,
    postCancelReservationForWorkplaceRequest,
    postReserveWorkplacesRequest,
    getWorkplaceMobileSummaryRequest,
    patchProductStatusRequest,
    getSummaryByBranch,
    getSummaryBySalesOffice,
    getDisasterAreaSummaryByBranch,
    getDisasterAreaSummaryBySalesOffice,
  } = useWaWorkplaceRepo();

  const { getStatusesOptionsRequest, getOrganizationOptionsRequest } = useWaOptionsRepo();

  const { user } = useWaUserStore();
  const { selectedDisaster } = useWaDisasterStore();

  const fetchWorkplaces = useCallback(
    async (overrideParams?: Partial<WaWorkplaceFilterParameters>) => {
      if (!selectedDisaster) return;

      const mergedParams = {
        ...workplaceFilterParameters,
        ...overrideParams,
      };

      const hasCustomerSearch = Boolean(mergedParams.customerId?.trim());

      // When customer_id is specified, exclude FE/site search parameters (status, reservation status, sales office, etc.)
      const paramsToUse: WaWorkplaceRequestParams = {
        branchCode: mergedParams.branchCode,
        disasterId: selectedDisaster.id,
        ...(hasCustomerSearch && { customerId: mergedParams.customerId! }),
        ...(!hasCustomerSearch && {
          statusList: operationStatusCodes(mergedParams.statusList),
        }),
        ...(!hasCustomerSearch &&
          mergedParams.reserved !== undefined && { reserved: mergedParams.reserved }),
        ...(!hasCustomerSearch &&
          mergedParams.salesOfficeCode && { salesOfficeCode: mergedParams.salesOfficeCode }),
        ...(!hasCustomerSearch &&
          mergedParams.sectionCode && { sectionCode: mergedParams.sectionCode }),
        ...(!hasCustomerSearch && mergedParams.squadCode && { squadCode: mergedParams.squadCode }),
      };

      await getWorkplacesRequest(paramsToUse).then((unaddressedWorkplaces) => {
        setWorkplaces(unaddressedWorkplaces);
      });
    },
    [getWorkplacesRequest, selectedDisaster, setWorkplaces, workplaceFilterParameters]
  );

  const reloadhWorkplace = useCallback(
    async (disasterId: string, branchCode: string, customerId: string) => {
      const params: WaWorkplaceRequestParams = { disasterId, branchCode, customerId };
      try {
        await getWorkplacesRequest(params).then((workplaces) => {
          setSelectedWorkplace(workplaces[0]);
        });
      } catch (error) {
        console.error(error);
      }
    },
    [getWorkplacesRequest, setSelectedWorkplace]
  );

  const toBranchOption = ({ code, name }: WaOrganizationBranchOption): WaBranchOption => ({
    code,
    name,
  });

  const toSalesOfficeOption = ({
    code,
    name,
    aliasBranchCode,
    aliasSalesOfficeName,
    location,
    defaultScale,
    searchRange,
  }: WaOrganizationSalesOfficeOption): WaSalesOfficeOption => ({
    code,
    name,
    aliasBranchCode,
    aliasSalesOfficeName,
    ...(location !== undefined && { location }),
    ...(defaultScale !== undefined && { defaultScale }),
    ...(searchRange !== undefined && { searchRange }),
  });

  const toSectionOption = ({
    code,
    name,
    location,
    defaultScale,
    searchRange,
  }: WaOrganizationSectionOption): WaSectionOption => ({
    code,
    name,
    ...(location !== undefined && { location }),
    ...(defaultScale !== undefined && { defaultScale }),
    ...(searchRange !== undefined && { searchRange }),
  });

  const toSquadOption = ({ code, name }: WaOrganizationSquadOption): WaSquadOption => ({
    code,
    name,
  });

  const fetchBranchOptions = useCallback(async () => {
    const options = await getOrganizationOptionsRequest();

    const populateHierarchyFromResponse = (response: WaOrganizationOptions) => {
      const branches = response.branches ?? [];
      setBranchOptions(branches.map(toBranchOption));
    };

    populateHierarchyFromResponse(options);
  }, [getOrganizationOptionsRequest, setBranchOptions]);

  const fetchSalesOfficeOptions = useCallback(
    async (branchCode: string) => {
      const options = await getOrganizationOptionsRequest(branchCode);

      const branch = options.branches?.find(
        (optionBranch: WaOrganizationBranchOption) => optionBranch.code === branchCode
      );

      const salesOffices = branch?.salesOffices ?? [];
      const formattedOptions = salesOffices.map(toSalesOfficeOption);

      setSalesOfficeOptions(formattedOptions);

      return formattedOptions;
    },
    [getOrganizationOptionsRequest, setSalesOfficeOptions]
  );

  const fetchSectionOptions = useCallback(
    async (branchCode: string, salesOfficeCode: string) => {
      return await getOrganizationOptionsRequest(branchCode, salesOfficeCode).then((options) => {
        const branch = options.branches?.find(
          (optionBranch: WaOrganizationBranchOption) => optionBranch.code === branchCode
        );

        const salesOffice = branch?.salesOffices.find(
          (optionSalesOffice: WaOrganizationSalesOfficeOption) =>
            optionSalesOffice.code === salesOfficeCode
        );

        const sections = salesOffice?.sections ?? [];
        const formattedOptions = sections.map(toSectionOption);
        setSectionOptions(formattedOptions);

        return formattedOptions;
      });
    },
    [getOrganizationOptionsRequest, setSectionOptions]
  );

  const fetchSquadOptions = useCallback(
    async (branchCode: string, salesOfficeCode: string, sectionCode: string) => {
      await getOrganizationOptionsRequest(branchCode, salesOfficeCode, sectionCode).then(
        (options) => {
          const branch = options.branches?.find(
            (optionBranch: WaOrganizationBranchOption) => optionBranch.code === branchCode
          );

          const salesOffice = branch?.salesOffices.find(
            (optionSalesOffice: WaOrganizationSalesOfficeOption) =>
              optionSalesOffice.code === salesOfficeCode
          );

          const section = salesOffice?.sections.find(
            (optionSection: WaOrganizationSectionOption) => optionSection.code === sectionCode
          );

          const squads = section?.squads ?? [];
          setSquadOptions(squads.map(toSquadOption));
        }
      );
    },
    [getOrganizationOptionsRequest, setSquadOptions]
  );

  const fetchStatusOptions = useCallback(async () => {
    await getStatusesOptionsRequest().then((options) => setStatusOptions(options));
  }, [getStatusesOptionsRequest, setStatusOptions]);

  const queueWorkplacesRefresh = () => {
    void Promise.resolve()
      .then(() => fetchWorkplaces())
      .catch((error) => {
        console.log(error);
      });
  };

  const fetchSalesOffice = useCallback(
    async (branchCode: string, salesOfficeCode: string) => {
      let branch: WaOrganizationBranchOption | undefined;
      if (branchCode === '30' && salesOfficeCode === '306680') {
        const okinichiBranchCode = '  '; // For Okinichi user, need to adjust branch code
        const options = await getOrganizationOptionsRequest(okinichiBranchCode);

        branch = options.branches?.find(
          (option: WaOrganizationBranchOption) => option.code === okinichiBranchCode
        );
      } else {
        const options = await getOrganizationOptionsRequest(branchCode);

        branch = options.branches?.find(
          (option: WaOrganizationBranchOption) => option.code === branchCode
        );
      }

      const salesOffices = branch?.salesOffices ?? [];
      return salesOffices.find((option) => option.code === salesOfficeCode);
    },
    [getOrganizationOptionsRequest]
  );

  const applyReservationUpdates = (reservations?: ReservationResponse[] | null) => {
    if (!reservations?.length) return;

    reservations.forEach((reservation) => {
      const customerId = reservation?.workplace?.customerId;
      const statusId = reservation?.workplace?.details?.statusId;
      if (!customerId || typeof statusId !== 'string') return;

      updateWorkplacesStatus(customerId, statusId);
    });
  };

  const buildReservationRequestParams = (
    engineerUserId: string,
    branchCode: string,
    customerId: string
  ): ReservationParameters | undefined => {
    if (!user || !selectedDisaster || !engineerUserId) return;

    return {
      engineerUserId,
      reserverUserId: user.id,
      disasterId: selectedDisaster.id,
      branchCode,
      customerId,
    };
  };

  const validateReservations = (reservations: WorkplaceReservationValidation[]) => {
    const validReservations = reservations
      .map((reservation) => {
        if (!reservation.engineerUserId) return undefined;

        return buildReservationRequestParams(
          reservation.engineerUserId,
          reservation.branchCode,
          reservation.customerId
        );
      })
      .filter((reservation): reservation is ReservationParameters => reservation !== undefined);

    return { validReservations };
  };

  const createWorkplaceReservationWithoutRefresh = async (
    reservations: WorkplaceReservationToCreate[]
  ) => {
    const { validReservations } = validateReservations(reservations);

    if (!validReservations.length) {
      return;
    }

    try {
      const response = await postReserveWorkplacesRequest(
        validReservations.map((validReservation) => ({
          engineerUserId: validReservation.engineerUserId,
          reserverUserId: validReservation.reserverUserId,
          disasterId: validReservation.disasterId,
          branchCode: validReservation.branchCode,
          customerId: validReservation.customerId,
        }))
      );
      applyReservationUpdates(response);
      return response;
    } catch (error) {
      console.error('Error creating workplace reservation:', error);
      throw error;
    }
  };

  const createWorkplaceReservation = async (reservations: WorkplaceReservationToCreate[]) => {
    try {
      const response = await createWorkplaceReservationWithoutRefresh(reservations);
      return response;
    } finally {
      queueWorkplacesRefresh();
    }
  };

  const cancelWorkplaceReservation = async (
    reservationsDetails: WorkplaceReservationsToCancel[]
  ) => {
    if (!reservationsDetails.length) {
      queueWorkplacesRefresh();
      return;
    }

    const reservationsToCancel = reservationsDetails.flatMap(
      ({ branchCode, customerId, reservations }) =>
        reservations.map((reservation) => ({
          branchCode,
          customerId,
          engineerUserId: reservation.engineerUserId,
        }))
    );

    const { validReservations } = validateReservations(reservationsToCancel);

    try {
      const response = await postCancelReservationForWorkplaceRequest(validReservations);
      applyReservationUpdates(response);
      return response;
    } catch (error) {
      console.error('Error canceling workplace reservation:', error);
      throw error;
    } finally {
      queueWorkplacesRefresh();
    }
  };

  const fetchWorkplaceMobileSummary = async (parameters: SummaryParameters) => {
    return await getWorkplaceMobileSummaryRequest(parameters).then((summary) =>
      setSummary(summary)
    );
  };

  const updateProductStatus = async (
    customerId: string,
    branchCode: string,
    productNumbers: string[],
    newStatusId: string,
    newComment?: string
  ) => {
    if (!selectedDisaster) return;

    return await patchProductStatusRequest({
      disasterId: selectedDisaster.id,
      customerId,
      branchCode,
      productNumbers,
      newStatusId,
      ...(newComment !== undefined && { newComment }),
    }).then((result) => {
      if (result?.workplace) {
        // When error occurs, result might by empty
        setSelectedWorkplace(result.workplace);
        return result.workplace;
      }

      return null;
    });
  };

  const fetchSummaryByBranch = useCallback(
    async (disasterId: string, branchCode?: string): Promise<BranchSituation[]> => {
      const data = await getSummaryByBranch({
        disasterId,
        ...(branchCode !== undefined && { branchCode }),
      });
      if (!data || !Array.isArray(data) || data.length === 0) return [];

      try {
        if (!branchCode) {
          const totalOverall = calculateTotalSummary(data, t('wards.desktop.summary.all'));
          const firstRow = mapSingleBranchToSituation(totalOverall);

          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const branchGroups = data.reduce((acc: Record<string, any[]>, curr) => {
            const code = curr.aliasBranchCode || 'unknown';
            if (!acc[code]) acc[code] = [];
            acc[code].push(curr);
            return acc;
          }, {});

          const branchSummaryRows = Object.entries(branchGroups)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([, group]) => {
              if (group[0].aliasBranchCode === '  ') group[0].branchName = '沖日';
              const summary = calculateTotalSummary(
                group,
                group[0].branchName || group[0].aliasBranchCode
              );
              return mapSingleBranchToSituation(summary);
            });
          return [firstRow, ...branchSummaryRows];
        }

        if (data[0].aliasBranchCode === '  ') data[0].branchName = '沖日';
        const branchTotalData = calculateTotalSummary(data, data[0]?.branchName || branchCode);
        const firstRow = mapSingleBranchToSituation(branchTotalData);

        const sortedData = [...data].sort((a, b) =>
          (a.salesOfficeCode || '').localeCompare(b.salesOfficeCode || '')
        );
        const otherRows = sortedData.map((item) => mapSingleBranchToSituation(item));

        return [firstRow, ...otherRows];
      } catch (e) {
        console.error('Mapping Error in Service:', e);
        return [];
      }
    },
    [getSummaryByBranch]
  );

  const fetchSummaryBySalesOffice = useCallback(
    async (
      disasterId: string,
      branchCode: string,
      salesOfficeCode?: string
    ): Promise<BusinessSituation[]> => {
      const data = await getSummaryBySalesOffice({
        disasterId,
        branchCode,
        salesOffice: salesOfficeCode || '',
      });

      if (!data || !Array.isArray(data) || data.length === 0) return [];

      try {
        const officeNameLabel = data[0]!.salesOfficeName;

        const totalDataRaw = calculateTotalSalesSummary(data, officeNameLabel);

        const firstRow = mapSingleItemToBusinessSituation(totalDataRaw);

        const sortedData = [...data].sort((a, b) =>
          (a.sectionCode || '').localeCompare(b.sectionCode || '')
        );
        const sectionRows = sortedData.map((item) => mapSingleItemToBusinessSituation(item));

        return [firstRow, ...sectionRows];
      } catch (e) {
        console.error('Mapping Error in SalesOffice Service:', e);
        return [];
      }
    },
    [getSummaryBySalesOffice]
  );

  const fetchUserSalesOffice = useCallback(
    async (userBranchCode: string, userSalesOfficeCode: string) => {
      try {
        const officeOptions = await fetchSalesOfficeOptions(userBranchCode);
        const sectionOptions = await fetchSectionOptions(userBranchCode, userSalesOfficeCode);

        // User's office location is determined in the following priority order: Section > Sales Office.
        const foundSection = sectionOptions.find((section) => section.code === user?.sectionCode);
        if (foundSection && foundSection.location) {
          setUserOfficeLocation(foundSection.location);
          return;
        }

        const foundOffice = officeOptions.find((office) => office.code === userSalesOfficeCode);
        if (foundOffice && foundOffice.location) {
          setUserOfficeLocation(foundOffice.location);
        }
      } catch (error) {
        console.error('Failed to find user office location:', error);
      }
    },
    [fetchSalesOfficeOptions, setUserOfficeLocation, fetchSectionOptions, user?.sectionCode]
  );

  const fetchDisasterAreaBranches = useCallback(
    async (disasterId: string): Promise<string[]> => {
      const data = await getDisasterAreaSummaryByBranch(disasterId);

      if (!data || !Array.isArray(data)) return [];

      try {
        return data.map((item: WaDisasterAreaBranchResponse) => item.aliasBranchCode);
      } catch (e) {
        console.error('Error mapping disaster area branches:', e);
        return [];
      }
    },
    [getDisasterAreaSummaryByBranch]
  );

  const fetchDisasterAreaSalesOffices = useCallback(
    async (disasterId: string, branchCode: string): Promise<string[]> => {
      const data = await getDisasterAreaSummaryBySalesOffice(disasterId, branchCode);

      if (!data || !Array.isArray(data)) return [];

      return data;
    },
    [getDisasterAreaSummaryBySalesOffice]
  );

  return {
    fetchWorkplaces,
    reloadhWorkplace,
    fetchBranchOptions,
    fetchStatusOptions,
    fetchSalesOfficeOptions,
    fetchSectionOptions,
    fetchSquadOptions,
    fetchSalesOffice,
    createWorkplaceReservationWithoutRefresh,
    createWorkplaceReservation,
    cancelWorkplaceReservation,
    fetchWorkplaceMobileSummary,
    updateProductStatus,
    fetchSummaryByBranch,
    fetchSummaryBySalesOffice,
    fetchUserSalesOffice,
    fetchDisasterAreaBranches,
    fetchDisasterAreaSalesOffices,
  };
}

export default useWaWorkplaceService;
